import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import GamesNavbar from "../components/GamesNavbar";
import GameRoom from "../../components/GameRoom";
import { useRoomRealtime } from "../../hooks/UseRoomRealtime";
import { advanceStage, claimSeat, startStory, updateCharacter, assignItem, assignNote, selectEnding } from "../../services/JubenshaService";
import ItemSelect from "./components/ItemSelect";
import JubenshaMap from "./components/JubenshaMap";
import "./JubenshaRoom.css";

export default function JubenshaRoom() {
  const { room_id } = useParams();
  const history = useHistory();
  const { room, loading, error, reload, seatPresence } = useRoomRealtime(room_id);
  const [playerName, setPlayerName] = useState("");
  const [selectedSeat, setSelectedSeat] = useState(null);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);
  const [editAttrSeatIndex, setEditAttrSeatIndex] = useState(null);
  const [editStatSeatIndex, setEditStatSeatIndex] = useState(null);
  const [attrForm, setAttrForm] = useState({});
  const [statForm, setStatForm] = useState({});
  const [assignItemSeat, setAssignItemSeat] = useState(null);
  const [assignItemId, setAssignItemId] = useState("");
  const [assignNoteSeat, setAssignNoteSeat] = useState(null);
  const [assignNoteId, setAssignNoteId] = useState("");
  const [publishNoteId, setPublishNoteId] = useState("");
  const [endingModalOpen, setEndingModalOpen] = useState(false);
  const [activeEnding, setActiveEnding] = useState(null);
  const [showBackground, setShowBackground] = useState(false);
  const [showGmPanel, setShowGmPanel] = useState(() => {
    const saved = localStorage.getItem("jubenshaShowGmPanel");
    return saved === null ? false : saved === "true";
  });

  useEffect(() => {
    const saved = localStorage.getItem("playerName");
    if (saved) setPlayerName(saved);
  }, []);

  useEffect(() => {
    localStorage.setItem("jubenshaShowGmPanel", String(showGmPanel));
  }, [showGmPanel]);

  const timeline = room?.data?.timeline || [];
  const stageIndex = room?.data?.stageIndex ?? -1;
  const currentStage = stageIndex >= 0 ? (timeline[stageIndex] || {}) : {};
  const currentStory = (room?.data?.publicStory || []).find((s) => s.key === room?.data?.stage);
  const atFinalStage = stageIndex >= (timeline.length || 1) - 1;
  const endings = room?.data?.endings || room?.data?.template?.endings || [];
  const selectedEndingId = room?.data?.selectedEndingId || null;
  const canSelectEnding = room?.status === "finished" || atFinalStage;
  const stageKey = currentStage?.key || room?.data?.stage || "briefing";
  const storyStarted = stageIndex >= 0 && room?.status !== "waiting";
  const seatIconPlaceholder = "https://placehold.co/96x96?text=ROLE";

  const handleSeatAvatarError = (e) => {
    // prevent infinite onError loops if placeholder also fails
    if (e?.currentTarget?.dataset?.fallbackApplied) return;
    if (e?.currentTarget) {
      e.currentTarget.dataset.fallbackApplied = "1";
      e.currentTarget.src = seatIconPlaceholder;
    }
  };

  const handleSeatClick = (index) => {
    if (!room?.data?.seats) return;
    const seat = room.data.seats[index];
    if (seat.playerName) {
      history.push(`/games/jubensha/${room_id}/seat/${index}`);
      return;
    }
    setSelectedSeat(index);
    setShowNamePrompt(true);
  };

  const handleClaimSeat = async () => {
    if (selectedSeat === null) return;
    if (!playerName.trim()) {
      alert("Please enter your name");
      return;
    }
    try {
      localStorage.setItem("playerName", playerName);
      await claimSeat(room_id, selectedSeat, playerName.trim());
      setShowNamePrompt(false);
      history.push(`/games/jubensha/${room_id}/seat/${selectedSeat}`);
    } catch (err) {
      console.error("Failed to claim seat", err);
      alert(err.response?.data?.message || "Seat is unavailable");
    }
  };

  const handleStart = async () => {
    try {
      await startStory(room_id);
    } catch (err) {
      console.error("Failed to start story", err);
      alert(err.response?.data?.message || "Failed to start story");
    }
  };

  const handleAdvance = async () => {
    try {
      await advanceStage(room_id);
    } catch (err) {
      console.error("Failed to advance stage", err);
      alert(err.response?.data?.message || "Cannot advance stage");
    }
  };

  const copyRoomLink = () => {
    const roomUrl = `${window.location.origin}${process.env.PUBLIC_URL}/games/jubensha/${room_id}`;
    navigator.clipboard
      .writeText(roomUrl)
      .then(() => {
        setCopyLinkSuccess(true);
        setTimeout(() => setCopyLinkSuccess(false), 1800);
      })
      .catch((err) => {
        console.error("Failed to copy link", err);
        alert("Copy failed");
      });
  };

  const renderTimeline = () => {
    return (
      <div className="timeline-bar">
        {(timeline || []).map((stage, idx) => {
          const status =
            stageIndex < 0
              ? "upcoming"
              : idx < stageIndex
                ? "done"
                : idx === stageIndex
                  ? "current"
                  : "upcoming";
          return (
            <div key={stage.key} className={`timeline-step ${status}`}>
              <div className="step-dot" />
              <div className="step-label">{idx <= stageIndex ? stage.title : "???"}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const publicLog = room?.data?.publicLog || [];
  const availableItems = room?.data?.availableItems || room?.data?.items || [];
  const availableNotes = room?.data?.availableNotes || room?.data?.notes || [];
  const publicNotes = room?.data?.publicNotes || [];
  const availableAttributes = room?.data?.attributes || [];
  const availableStats = room?.data?.stats || [];
  const availableSharedTags = room?.data?.sharedTags || room?.data?.template?.sharedTags || [];
  const backgroundStory = room?.data?.backgroundStory || room?.data?.template?.backgroundStory || "";

  const stageMapEntry =
    room?.data?.map?.[stageKey] ||
    room?.data?.template?.map?.[stageKey] ||
    null;

  const handleOpenEnding = (ending) => {
    setActiveEnding(ending);
    setEndingModalOpen(true);
  };

  const handleConfirmEnding = async () => {
    if (!activeEnding) return;
    try {
      await selectEnding(room_id, activeEnding.id);
      setEndingModalOpen(false);
      // Room will update automatically via websocket (room:update)
    } catch (err) {
      console.error("Failed to select ending", err);
      alert(err.response?.data?.message || "Failed to select ending");
    }
  };

  const seatOptions = (room?.data?.seats || []).map((s, idx) => ({
    label: `${idx + 1} · ${s.characterName}${s.playerName ? ` (${s.playerName})` : ''}`,
    value: idx,
  }));

  const handleSaveAttribute = async () => {
    if (editAttrSeatIndex === null || !attrForm.key) return;
    try {
      const attrPayload = { [attrForm.key]: attrForm.value ?? 0 };
      await updateCharacter(room_id, editAttrSeatIndex, { attributes: attrPayload });
      setAttrForm({});
      setEditAttrSeatIndex(null);
      // Room will update automatically via socket
    } catch (err) {
      console.error("Failed to update character", err);
      alert(err.response?.data?.message || "Failed to update character");
    }
  };

  const handleSaveStat = async () => {
    if (editStatSeatIndex === null || !statForm.key) return;
    try {
      const statPayload = { [statForm.key]: statForm.value ?? 0 };
      await updateCharacter(room_id, editStatSeatIndex, { stats: statPayload });
      setStatForm({});
      setEditStatSeatIndex(null);
      // Room will update automatically via socket
    } catch (err) {
      console.error("Failed to update character", err);
      alert(err.response?.data?.message || "Failed to update character");
    }
  };

  const handleAssignItem = async () => {
    if (assignItemSeat === null || !assignItemId) return;
    try {
      await assignItem(room_id, assignItemSeat, assignItemId);
      setAssignItemSeat(null);
      setAssignItemId("");
      // Room will update automatically via socket
    } catch (err) {
      console.error("Failed to assign item", err);
      alert(err.response?.data?.message || "Failed to assign item");
    }
  };

  const handleAssignNote = async () => {
    if (!assignNoteId || assignNoteSeat === null) return;
    try {
      await assignNote(room_id, assignNoteId, 'private', assignNoteSeat);
      setAssignNoteSeat(null);
      setAssignNoteId("");
      // Room will update automatically via socket
    } catch (err) {
      console.error("Failed to assign note", err);
      alert(err.response?.data?.message || "Failed to assign private note");
    }
  };

  const handlePublishNote = async () => {
    if (!publishNoteId) return;
    try {
      await assignNote(room_id, publishNoteId, 'public');
      setPublishNoteId("");
      // Room will update automatically via socket
    } catch (err) {
      console.error("Failed to publish note", err);
      alert(err.response?.data?.message || "Failed to publish note");
    }
  };

  return (
    <GameRoom room={room} loading={loading} error={error} onRetry={reload}>
      {(roomData) => (
        <div className="jubensha-room">
          <GamesNavbar />

          <div className="jubensha-room-content">
            <div className="room-header">
              <div>
                <h1 className="room-title">{room.name}</h1>
                <div className="room-subtitle">
                  {room.data?.templateTitle} · {room.data?.setting?.vibe}
                </div>
                {backgroundStory && (
                  <div className="room-background">
                    <div className={`room-background-text ${showBackground ? "expanded" : ""}`}>
                      {backgroundStory}
                    </div>
                    <button
                      type="button"
                      className="room-background-toggle"
                      onClick={() => setShowBackground((v) => !v)}
                    >
                      {showBackground ? "Hide background" : "Show background"}
                    </button>
                  </div>
                )}
              </div>
              <div className="room-actions">
                <span className={`status-pill ${room.status}`}>{room.status}</span>
                <button className="btn-secondary" onClick={copyRoomLink}>
                  {copyLinkSuccess ? "Copied" : "Share link"}
                </button>
              </div>
            </div>

            <div className="stage-card">
              <div className="stage-top">
                <div>
                  <div className="stage-label">Current Stage</div>
                  <div className="stage-name">{currentStage.title || "Not started"}</div>
                </div>
                <div className="stage-actions">
                  {room.status === "waiting" && (
                    <button className="btn-primary" onClick={handleStart}>
                      Start Story
                    </button>
                  )}
                  {room.status !== "finished" && room.status !== "waiting" && (
                    <button className="btn-primary" onClick={handleAdvance} disabled={atFinalStage}>
                      Next Stage
                    </button>
                  )}
                </div>
              </div>
              {renderTimeline()}
              {storyStarted && currentStory?.text && <p className="stage-text">{currentStory.text}</p>}
            </div>

            {storyStarted && (
              <div className="stage-card">
                <JubenshaMap
                  title="Game Map"
                  subtitle={`Map · ${currentStage.title || stageKey}`}
                  mapEntry={stageMapEntry}
                  availableItems={availableItems}
                  availableNotes={availableNotes}
                  availableTags={availableSharedTags}
                  variant="covered"
                  isRoomView
                />
              </div>
            )}

            <div className="seats-grid">
              {(room.data?.seats || []).map((seat, idx) => {
                const isOccupied = Boolean(seat.playerName);
                const present = seatPresence?.includes(idx);
                const seatBioText = Array.isArray(seat.bio) ? (seat.bio[0]?.content || '') : seat.bio;
                const templateCharacterIcon =
                  room?.data?.template?.characters?.find(
                    (c) => c?.id === seat?.characterId || c?.name === seat?.characterName,
                  )?.iconUrl || null;
                const seatIcon = seat.iconUrl || seat.characterIcon || templateCharacterIcon || seatIconPlaceholder;
                return (
                  <div key={idx} className={`seat-card ${isOccupied ? "occupied" : "empty"}`} onClick={() => handleSeatClick(idx)}>
                    <div className="seat-head">
                      <div className="seat-title">Seat {idx + 1}</div>
                      <div className={`seat-presence ${present ? "online" : "offline"}`}>
                        <span className="dot" /> {present ? "Online" : "Offline"}
                      </div>
                    </div>
                    <div className="seat-body">
                      <img
                        className="character-avatar"
                        src={seatIcon}
                        alt={seat.characterName}
                        loading="lazy"
                        onError={handleSeatAvatarError}
                      />
                      <div className="seat-meta">
                        <div className="character-name">{seat.characterName}</div>
                        <div className="character-role">{seat.role || seatBioText}</div>
                        <div className="seat-stats-mini">
                          {Object.entries(seat.stats || {}).map(([stat, value]) => (
                            <div key={stat} className="mini-stat-row">
                              <span className="mini-stat-label">{stat}</span>
                              <div className="mini-stat-bar">
                                <div
                                  className="mini-stat-fill"
                                  style={{ width: `${Math.min(100, Math.max(0, (Number(value) / 10) * 100))}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {isOccupied ? (
                      <div className="player-row">
                        <div className="player-name">{seat.playerName}</div>
                      </div>
                    ) : (
                      <div className="seat-status">Tap to claim</div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="gm-panel">
              <div className="gm-panel-head">
                <div className="gm-panel-title">GM Control Panel</div>
                <button
                  type="button"
                  className="gm-panel-toggle"
                  onClick={() => setShowGmPanel((v) => !v)}
                >
                  {showGmPanel ? "Hide" : "Show"}
                </button>
              </div>
              {showGmPanel && (
                <div className="gm-grid">
                <div className="gm-block">
                  <div className="gm-block-title">Edit Attributes</div>
                  <div className="gm-row">
                    <select
                      className="gm-select"
                      value={editAttrSeatIndex !== null ? String(editAttrSeatIndex) : ''}
                      onChange={(e) => setEditAttrSeatIndex(e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">Select Seat</option>
                      {seatOptions.map((opt) => (
                        <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="gm-row gm-two-col">
                    <select
                      className="gm-select"
                      value={attrForm.key ?? ''}
                      onChange={(e) => setAttrForm({ ...attrForm, key: e.target.value || null })}
                    >
                      <option value="">Select Attribute</option>
                      {availableAttributes.map((attr) => (
                        <option key={attr} value={attr}>{attr}</option>
                      ))}
                    </select>
                    <input
                      className="gm-input"
                      type="number"
                      placeholder="Value"
                      value={attrForm.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? undefined : Number(e.target.value);
                        setAttrForm({ ...attrForm, value: val });
                      }}
                    />
                  </div>
                  <button className="btn-primary" onClick={handleSaveAttribute} disabled={editAttrSeatIndex === null || !attrForm.key || attrForm.value === undefined || attrForm.value === null}>
                    Save Attribute
                  </button>
                </div>

                <div className="gm-block">
                  <div className="gm-block-title">Edit Stats</div>
                  <div className="gm-row">
                    <select
                      className="gm-select"
                      value={editStatSeatIndex !== null ? String(editStatSeatIndex) : ''}
                      onChange={(e) => setEditStatSeatIndex(e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">Select Seat</option>
                      {seatOptions.map((opt) => (
                        <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="gm-row gm-two-col">
                    <select
                      className="gm-select"
                      value={statForm.key ?? ''}
                      onChange={(e) => setStatForm({ ...statForm, key: e.target.value || null })}
                    >
                      <option value="">Select Stat</option>
                      {availableStats.map((stat) => (
                        <option key={stat} value={stat}>{stat}</option>
                      ))}
                    </select>
                    <input
                      className="gm-input"
                      type="number"
                      placeholder="Value"
                      value={statForm.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? undefined : Number(e.target.value);
                        setStatForm({ ...statForm, value: val });
                      }}
                    />
                  </div>
                  <button className="btn-primary" onClick={handleSaveStat} disabled={editStatSeatIndex === null || !statForm.key || statForm.value === undefined || statForm.value === null}>
                    Save Stat
                  </button>
                </div>

                <div className="gm-block">
                  <div className="gm-block-title">Assign Item</div>
                  <div className="gm-row">
                    <select
                      className="gm-select"
                      value={assignItemSeat ?? ''}
                      onChange={(e) => setAssignItemSeat(e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">Select Seat</option>
                      {seatOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="gm-row">
                    <ItemSelect
                      value={assignItemId}
                      onChange={setAssignItemId}
                      items={availableItems}
                      placeholder="Select Item"
                    />
                  </div>
                  <button className="btn-primary" onClick={handleAssignItem} disabled={assignItemSeat === null || !assignItemId}>
                    Assign Item
                  </button>
                </div>

                <div className="gm-block">
                  <div className="gm-block-title">Assign / Publish Notes</div>
                  <div className="gm-row">
                    <select
                      className="gm-select"
                      value={assignNoteSeat ?? ''}
                      onChange={(e) => setAssignNoteSeat(e.target.value === '' ? null : Number(e.target.value))}
                    >
                      <option value="">Select Seat (Private)</option>
                      {seatOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="gm-row gm-two-col">
                    <select
                      className="gm-select"
                      value={assignNoteId}
                      onChange={(e) => setAssignNoteId(e.target.value)}
                    >
                      <option value="">Select Note (Private)</option>
                      {availableNotes.map((n) => (
                        <option key={n.id} value={n.id}>{n.title}</option>
                      ))}
                    </select>
                    <button className="btn-primary" onClick={handleAssignNote} disabled={assignNoteSeat === null || !assignNoteId}>
                      Assign Private
                    </button>
                  </div>
                  <div className="gm-row gm-two-col">
                    <select
                      className="gm-select"
                      value={publishNoteId}
                      onChange={(e) => setPublishNoteId(e.target.value)}
                    >
                      <option value="">Select Note to Publish</option>
                      {availableNotes.map((n) => (
                        <option key={n.id} value={n.id}>{n.title}</option>
                      ))}
                    </select>
                    <button className="btn-secondary" onClick={handlePublishNote} disabled={!publishNoteId}>
                      Publish to All
                    </button>
                  </div>
                </div>
              </div>
              )}
            </div>

            {storyStarted && (
              <div className="public-log">
              <div className="log-head">
                <h3>Story Log</h3>
                <span className="log-note">Auto-shares public beats with everyone</span>
              </div>
              {publicLog.length === 0 && <div className="log-empty">No events yet.</div>}
              {publicLog.map((entry, idx) => {
                const logType = entry.endingId ? "ending" : "stage";
                return (
                  <div key={idx} className="log-item" data-type={logType}>
                    <div className="log-title">{entry.title}</div>
                    <div className="log-meta">
                      {entry.stageKey || entry.endingId || entry.type} · {new Date(entry.at).toLocaleTimeString()}
                    </div>
                    <div className="log-text">{entry.text}</div>
                  </div>
                );
              })}
              </div>
            )}

            {canSelectEnding && endings.length > 0 && (
              <div className="stage-card">
                <div className="stage-top">
                  <div>
                    <div className="stage-label">Final Endings</div>
                    <div className="stage-name">
                      {selectedEndingId
                        ? endings.find((e) => e.id === selectedEndingId)?.title || "Ending Selected"
                        : "Choose an ending"}
                    </div>
                  </div>
                </div>
                <div className="endings-grid">
                  {endings.map((ending) => (
                    <button
                      key={ending.id}
                      className={`ending-card ${selectedEndingId === ending.id ? "ending-card-selected" : ""}`}
                      type="button"
                      onClick={() => handleOpenEnding(ending)}
                    >
                      <div className="ending-title">{ending.title}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {publicNotes.length > 0 && (
              <div className="public-log">
                <div className="log-head">
                  <h3>Public Notes</h3>
                </div>
                {publicNotes.map((note, idx) => (
                  <div key={idx} className="log-item">
                    <div className="log-title">{note.title}</div>
                    <div className="log-meta">{new Date(note.assignedAt || Date.now()).toLocaleString()}</div>
                    <div className="log-text">{note.message}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {showNamePrompt && (
            <div className="name-prompt-overlay">
              <div className="name-prompt-box">
                <h3>Claim this role</h3>
                <input
                  type="text"
                  className="form-input"
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  placeholder="Your name"
                  maxLength={24}
                />
                <div className="prompt-actions">
                  <button className="btn-primary" onClick={handleClaimSeat}>
                    Join Seat {selectedSeat !== null ? selectedSeat + 1 : ""}
                  </button>
                  <button className="btn-secondary" onClick={() => setShowNamePrompt(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {endingModalOpen && activeEnding && (
            <div className="ending-modal-overlay">
              <div className="ending-modal">
                <div className="ending-modal-head">
                  <div className="ending-modal-title">{activeEnding.title}</div>
                  <button
                    className="ending-modal-close"
                    onClick={() => setEndingModalOpen(false)}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&#10005;</span>
                  </button>
                </div>
                <div className="ending-modal-body">
                  <div className="ending-modal-description">{activeEnding.description}</div>
                </div>
                <div className="ending-modal-actions">
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={handleConfirmEnding}
                    disabled={selectedEndingId === activeEnding.id}
                  >
                    {selectedEndingId === activeEnding.id ? "Selected" : "Select this ending"}
                  </button>
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => setEndingModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </GameRoom>
  );
}
